/**
 * Admin Layout
 * Shared layout for all admin pages with sidebar navigation
 */
import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, Building2, Mail, BarChart3,
  LogOut, ChevronLeft, Sparkles
} from 'lucide-react';
import { auth } from '../../utils/auth';
import { toast } from 'sonner';

const SUPER_ADMINS = [
  'martin@kviteberg.no',
  'lexy@kviteberg.no'
];

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [collapsed, setCollapsed] = useState(false);
  
  useEffect(() => {
    const currentUser = auth.getUser();
    if (!currentUser) {
      toast.error('Du må være logget inn');
      navigate('/lexy/login');
      return;
    }
    
    const email = currentUser.email?.toLowerCase();
    if (!SUPER_ADMINS.includes(email)) {
      toast.error('Du har ikke tilgang til admin-panelet');
      navigate('/lexy/dashboard');
      return;
    }
    
    setUser(currentUser);
  }, [navigate]);
  
  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/invitations', icon: Mail, label: 'Invitasjoner' },
    { path: '/admin/tenants', icon: Building2, label: 'Firmaer' },
    { path: '/admin/stats', icon: BarChart3, label: 'Statistikk' },
  ];
  
  if (!user) return null;
  
  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 256 }}
        className="bg-slate-900 border-r border-slate-800 flex flex-col"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-white">Lexy Admin</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all
                ${isActive 
                  ? 'bg-violet-600 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }
              `}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
        
        {/* User section */}
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {user.name?.charAt(0) || 'A'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
            )}
          </div>
          
          <button
            onClick={() => navigate('/lexy/dashboard')}
            className="w-full flex items-center gap-3 px-3 py-2 mt-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="text-sm">Tilbake til Lexy</span>}
          </button>
        </div>
      </motion.aside>
      
      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
